'use client';

// 홈 광고 카드 캐러셀의 브라우저 쪽 동작 — 화면에는 숨은 표식 1개만 그린다.
//  ① 유료 카드끼리만 섞기(Fisher-Yates, 방문마다) — CSS order 로만 바꾼다(DOM 이동 없음 · 서버 HTML 은 고정 순서).
//     «광고주 모집» 카드는 언제나 맨 뒤.
//  ② ‹ › 버튼 = 한 화면 폭만큼 스크롤(스와이프는 브라우저 기본 + scroll-snap). 자동 넘김 없음.
//  ③ 노출 계측: 카드마다 뷰포트 50% 이상 1초 → ad_view(카드·경로당 30분 1회 — sessionStorage, PaidAdClient markSeenOnce) · 클릭 → ad_click. 모집 카드 클릭 → advertise_click(home_slot).
//  ④ 이미지 실패 → 단색 + 문구(data-broken) · 깨진 카드는 ad_view 를 보내지 않는다.
//  봇 UA·자동화 브라우저는 기록하지 않는다(sendAdEvent 가 lib/bot-classify 로 거른다). 화면은 모두 같다.

import { useEffect, useRef } from 'react';
import { sendAdEvent, markSeenOnce, adViewSeenKey, isBrokenCreative, actionOf } from './PaidAdClient';
import { trackAdvertiseClick } from '@/components/ads/acq/track';
import { shuffled } from '@/lib/ads/card-copy';

function propsOf(el: HTMLElement) {
  const d = el.dataset;
  if (!d.adCampaign || !d.adVersion) return null;
  return { campaignId: d.adCampaign, versionId: d.adVersion, placement: d.adPlacement ?? 'home', pagePath: window.location.pathname };
}

/** 유료 카드에 섞인 순서(order)를, 모집 카드에 뒤 순서를 매긴다. 테스트용으로 export. */
export function applyShuffleOrder(track: HTMLElement, rand: () => number = Math.random): void {
  const paid = Array.from(track.querySelectorAll<HTMLElement>('[data-pac-paid]'));
  const order = shuffled(paid.map((_, i) => i), rand);
  order.forEach((idx, pos) => { paid[idx].style.order = String(pos); });
  Array.from(track.querySelectorAll<HTMLElement>('[data-pac-recruit]')).forEach((el, i) => { el.style.order = String(100 + i); });
}

export default function PaidAdCarouselClient() {
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const root = ref.current?.closest<HTMLElement>('[data-paid-ad-root]');
    const track = root?.querySelector<HTMLElement>('[data-pac-track]');
    if (!root || !track) return;
    const cleanups: Array<() => void> = [];

    // ① 섞기
    applyShuffleOrder(track);
    track.scrollLeft = 0;

    // ④ 이미지 실패
    track.querySelectorAll<HTMLImageElement>('.pac__img img').forEach((img) => {
      const mark = () => img.parentElement?.setAttribute('data-broken', '1');
      if (img.complete && img.naturalWidth === 0) mark();
      img.addEventListener('error', mark, { once: true });
      cleanups.push(() => img.removeEventListener('error', mark));
    });

    // ② 버튼
    const prev = root.querySelector<HTMLButtonElement>('[data-pac-prev]');
    const next = root.querySelector<HTMLButtonElement>('[data-pac-next]');
    const smooth = (() => { try { return !window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return true; } })();
    const sync = () => {
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
    };
    const go = (dir: 1 | -1) => () => {
      try { track.scrollBy({ left: dir * track.clientWidth, behavior: smooth ? 'smooth' : 'auto' }); } catch { track.scrollLeft += dir * track.clientWidth; }
    };
    const onPrev = go(-1);
    const onNext = go(1);
    prev?.addEventListener('click', onPrev);
    next?.addEventListener('click', onNext);
    let raf = 0;
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(() => { raf = 0; sync(); }); };
    track.addEventListener('scroll', onScroll, { passive: true });
    sync();
    cleanups.push(() => {
      prev?.removeEventListener('click', onPrev);
      next?.removeEventListener('click', onNext);
      track.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    });

    // ③ 카드별 노출(50% · 1초 · 1회)
    const timers = new Map<Element, ReturnType<typeof setTimeout>>();
    if (typeof IntersectionObserver !== 'undefined') {
      const io = new IntersectionObserver((es) => {
        for (const e of es) {
          const el = e.target as HTMLElement;
          const on = e.isIntersecting && e.intersectionRatio >= 0.5;
          const t = timers.get(el);
          if (!on) { if (t) { clearTimeout(t); timers.delete(el); } continue; }
          if (t) continue;
          timers.set(el, setTimeout(() => {
            timers.delete(el);
            const p = propsOf(el);
            if (!p) return;
            if (isBrokenCreative(el)) return; // 이미지 실패 카드는 노출로 세지 않는다
            if (markSeenOnce(adViewSeenKey(p.versionId, p.pagePath))) sendAdEvent('ad_view', p);
          }, 1000));
        }
      }, { threshold: [0, 0.5, 1] });
      track.querySelectorAll<HTMLElement>('[data-pac-paid]').forEach((el) => io.observe(el));
      cleanups.push(() => { io.disconnect(); timers.forEach((t) => clearTimeout(t)); timers.clear(); });
    }

    // ③ 클릭
    const onClick = (e: MouseEvent) => {
      const a = (e.target as HTMLElement | null)?.closest?.('a');
      if (!a || !track.contains(a)) return;
      if (a.hasAttribute('data-pac-recruit')) { trackAdvertiseClick(a.getAttribute('data-pac-from') || 'home_slot'); return; }
      // 유료 카드 — 카드 자체(a)이거나 «홈페이지 + 전화» 감싼 칸 안의 링크(본 링크 · 「전화 문의」 = action 'call')
      const card = a.closest<HTMLElement>('[data-pac-paid]');
      if (card && a.hasAttribute('data-paid-ad-link')) {
        const p = propsOf(card);
        if (p) sendAdEvent('ad_click', { ...p, ...actionOf(a) });
      }
    };
    track.addEventListener('click', onClick);
    cleanups.push(() => track.removeEventListener('click', onClick));
    // 머리 「광고하기」 버튼 클릭 → advertise_click(home_head)
    const apply = root?.querySelector<HTMLElement>('[data-pac-apply]');
    if (apply) {
      const onApply = () => trackAdvertiseClick('home_head');
      apply.addEventListener('click', onApply);
      cleanups.push(() => apply.removeEventListener('click', onApply));
    }

    return () => cleanups.forEach((f) => f());
  }, []);

  return <span ref={ref} hidden aria-hidden="true" data-paid-ad-probe="" />;
}
